/**
 * pages/wishlist-home.tsx
 *
 * /wishlist — 2×3 vibe grid with total item count and FAB.
 * Tapping a vibe card navigates to /wishlist/:vibe.
 * FAB opens QuickAddSheet with vibe-picker enabled.
 * Bottom nav with Wishlist tab active.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { WishlistVibe } from '@just-us/shared'
import { useWishlist, useWishlistDone, useCreateWishlistItem } from '../hooks/use-wishlist'
import { useCouple } from '../hooks/use-couple'
import { getAuthPayload } from '../store/auth'
import BottomNav from '../components/bottom-nav'
import VibeCard from '../components/wishlist/vibe-card'
import QuickAddSheet from '../components/wishlist/quick-add-sheet'

const ALL_VIBES: WishlistVibe[] = ['wander', 'watch', 'taste', 'weekend', 'try', 'together']

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

const VibeCardSkeleton = () => (
  <div className="rounded-[20px] bg-[#F9E0E8] border border-[#F4C0D1] p-4 animate-shimmer h-[110px]" />
)

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function WishlistHomePage() {
  const navigate = useNavigate()
  const [showAdd, setShowAdd] = useState(false)

  const { data: wishlistData, isLoading } = useWishlist()
  const { data: doneData } = useWishlistDone()
  const { data: coupleData } = useCouple()
  const createItem = useCreateWishlistItem()

  const payload = getAuthPayload()
  const users = coupleData?.data?.users ?? []
  const partner = users.find((u) => u.id !== payload?.user_id)
  const partnerNickname = partner?.nickname ?? 'your partner'

  const allItems = wishlistData?.data?.items ?? []
  const pendingItems = allItems
  const doneCount = doneData?.data?.total ?? 0

  return (
    <div className="min-h-screen bg-[#FAF8F4] flex flex-col max-w-[430px] mx-auto relative">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#FAF8F4] pt-[env(safe-area-inset-top)] border-b border-[#F9E0E8]">
        <div className="flex items-center px-5 h-[56px]">
          <span className="font-['Playfair_Display'] text-[20px] font-medium text-[#2C1810]">
            Our Wishlist 🌟
          </span>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto px-5 pt-4 pb-[88px]">
        {isLoading ? (
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <VibeCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <>
            {/* Stats pill */}
            {pendingItems.length > 0 && (
              <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2] mb-4">
                {pendingItems.length} thing{pendingItems.length === 1 ? '' : 's'} to do together
              </p>
            )}

            {/* 2×3 vibe grid */}
            <div className="grid grid-cols-2 gap-3">
              {ALL_VIBES.map((vibe) => (
                <VibeCard
                  key={vibe}
                  vibe={vibe}
                  items={allItems.filter((i) => i.vibe === vibe)}
                  partnerNickname={partnerNickname}
                />
              ))}
            </div>

            {/* Done together row */}
            <button
              type="button"
              onClick={() => navigate('/wishlist/done')}
              className="
                mt-3 w-full flex items-center justify-between
                px-4 h-[48px] rounded-[16px]
                bg-[#FBEAF0] border border-[#F4C0D1]
                active:opacity-60 transition-opacity duration-100
                touch-manipulation
              "
            >
              <div className="flex items-center gap-2">
                <span className="text-[18px]">🫙</span>
                <span className="font-['DM_Sans'] text-[14px] text-[#D4537E] font-medium">
                  Done together
                </span>
                {doneCount > 0 && (
                  <span className="font-['DM_Sans'] text-[12px] text-[#B89AA2]">
                    · {doneCount}
                  </span>
                )}
              </div>
              <span className="font-['DM_Sans'] text-[14px] text-[#D4537E]">›</span>
            </button>

            {/* Empty state */}
            {allItems.length === 0 && !isLoading && (
              <div className="flex flex-col items-center justify-center pt-12 gap-3 animate-fade-in">
                <div className="text-[52px] animate-float">🌟</div>
                <p className="font-['Playfair_Display'] text-[18px] font-medium text-[#2C1810]">
                  Your wishlist awaits
                </p>
                <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2] text-center px-8">
                  Add things you want to do, watch, taste, and experience together.
                </p>
              </div>
            )}
          </>
        )}
      </main>

      {/* FAB */}
      <button
        type="button"
        onClick={() => setShowAdd(true)}
        className="
          fixed bottom-[80px] right-5 max-w-[430px]
          w-[56px] h-[56px] rounded-full
          bg-[#D4537E] text-white text-[24px]
          shadow-[0_4px_20px_rgba(212,83,126,0.4)]
          flex items-center justify-center
          active:scale-90 transition-all duration-150
          touch-manipulation z-30
        "
        aria-label="Add to wishlist"
      >
        +
      </button>

      <BottomNav />

      {/* Quick-add sheet */}
      {showAdd && (
        <QuickAddSheet
          defaultVibe="wander"
          allowVibeChange
          isSubmitting={createItem.isPending}
          onAdd={(data) => {
            createItem.mutate(data, { onSuccess: () => setShowAdd(false) })
          }}
          onClose={() => setShowAdd(false)}
        />
      )}
    </div>
  )
}
